/* eslint-disable prettier/prettier */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from, of, throwError } from 'rxjs';
import { map, tap, catchError, switchMap } from 'rxjs/operators';
import {
  CognitoIdentityProviderClient,
  ListUsersCommand,
  AdminCreateUserCommand,
  AdminUpdateUserAttributesCommand,
  AdminDeleteUserCommand,
  AdminSetUserPasswordCommand,
  AdminAddUserToGroupCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { HttpClient } from '@angular/common/http';
import * as CryptoJS from 'crypto-js';
import { Router } from '@angular/router';
import { RegionService } from 'src/app/shared/components/menu-dashboard/service/region.service';
//import { environment } from '@env/environment';
import { jwtDecode } from 'jwt-decode';

export interface User {
  entityId: string;
  entityName?: string;
  clientId: string;
  id: string;
  username: string;
  email: string;
  role: string;
  password?: string;
  forcePasswordChange?: boolean;
}

interface DecodedIdToken {
  'custom:client-id'?: string;
  [key: string]: any;
}

const EVOLUTION_CATEGORY_STORAGE_KEY = 'evolution.selectedCategory';
const EVOLUTION_TIME_RANGE_STORAGE_KEY = 'evolution.selectedTimeRange';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUserSubject: BehaviorSubject<User | null>;
  public currentUser: Observable<User | null>;
  private client: CognitoIdentityProviderClient;
  //private region = 'eu-west-1';
  //private userPoolId = 'eu-west-1_dNyhcA2PB';
  //private identityPoolId = 'eu-west-1:d071996b-45f7-4402-ada5-09d6b2aca792';
  //private clientId = '77bevedldlqhglo4g5hfd479li';
  //private cognitoDomain = 'https://ciberlens-auth-test.auth.eu-west-1.amazoncognito.com';
  //private redirectUri = 'http://localhost:4200/callback'; //Si se hace pruebas locales
  //private redirectUriLogout = 'http://localhost:4200/logout'; //Si se hace pruebas locales
  //private redirectUri = 'https://ciberlens.grupoonetec.com/callback'; //Si se sube a pre
  //private redirectUriLogout = 'https://ciberlens.grupoonetec.com/logout'; //Si se sube a pre
  
  // Timer para refresh preventivo
  private refreshTimer?: any;

  private region = 'eu-south-2';
  private userPoolId = 'eu-south-2_UFgbLVCTC';
  private identityPoolId = 'eu-south-2:ba1c6446-f719-4356-8333-b24b2b1afdaf';
  private clientId = '22tc4gtprdhiogifou5efp80u5';
  private cognitoDomain = 'https://ciberlens-auth-prod.auth.eu-south-2.amazoncognito.com';
  private redirectUri = 'https://poc.ciberlens.com/callback'; //Si se sube a pro
  private redirectUriLogout = 'https://poc.ciberlens.com/logout'; //Si se sube a pro

  

  constructor(
    private http: HttpClient,
    private router: Router,
    private regionService: RegionService
  ) {
    this.client = new CognitoIdentityProviderClient({
      region: this.region,
    });

    const storedUser = localStorage.getItem('currentUser');
    const initialUser = storedUser ? JSON.parse(storedUser) : null;
    this.currentUserSubject = new BehaviorSubject<User | null>(initialUser);
    this.currentUser = this.currentUserSubject.asObservable();

    this.restoreSession();
  }

  // 🔽 Helper para normalizar email (minúsculas + trim)
  private normalizeEmail(email: string): string {
    return (email || '').trim().toLowerCase();
  }

  // Método para restaurar la sesión
  async restoreSession(): Promise<void> {
    const idToken = localStorage.getItem('idToken');
    const storedUser = localStorage.getItem('currentUser');

    if (idToken && storedUser) {
      try {
        const user: User = JSON.parse(storedUser);
        console.log('Restaurando sesión para usuario:', user);

        // Verificar si el idToken sigue siendo válido (simplificado, podrías validar la expiración)
        const idTokenPayload = JSON.parse(atob(idToken.split('.')[1]));
        const exp = idTokenPayload.exp * 1000; // Expiración en milisegundos
        if (Date.now() > exp) {
          console.log('idToken expirado, intentando refrescar');
          await this.refreshSession();
          return;
        }

        // Restaurar usuario
        this.currentUserSubject.next(user);

        // Restaurar credenciales temporales
        await this.obtainTemporaryCredentials(idToken);
        this.schedulePreventiveRefresh(idToken);
        console.log('Sesión restaurada exitosamente');
      } catch (error) {
        console.error('Error al restaurar sesión:', error);
        this.logout(); // Limpiar sesión si falla
      }
    } else {
      console.log('No se encontró sesión válida en localStorage');
    }
  }

  // Método para refrescar la sesión usando refresh_token
  async refreshSession(): Promise<void> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      console.error('No refresh token disponible');
      this.logout();
      return;
    }

    const tokenUrl = `${this.cognitoDomain}/oauth2/token`;
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: this.clientId,
      refresh_token: refreshToken,
    });

    try {
      const response = await this.http
        .post(tokenUrl, body.toString(), {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        })
        .toPromise();

      const oldIdToken = localStorage.getItem('idToken')!;
      const oldAccessToken = localStorage.getItem('accessToken')!;

      const idToken = (response as any).id_token || oldIdToken;
      const accessToken = (response as any).access_token || oldAccessToken;

      if (!idToken || !accessToken) {
        throw new Error('No tokens recibidos al refrescar');
      }

      const idTokenPayload = JSON.parse(atob(idToken.split('.')[1]));
      const appUser: User = {
        id: idTokenPayload['sub'] || '0',
        username: idTokenPayload['email'] || 'unknown',
        email: idTokenPayload['email'] || 'unknown',
        role: idTokenPayload['custom:ciso-level'] || 'USER',
        entityId: idTokenPayload['custom:entity'] || 'unknown',
        entityName: idTokenPayload['custom:entity'] || 'unknown',
        clientId: idTokenPayload['custom:client-id'] || 'unknown',
      };

      this.currentUserSubject.next(appUser);
      localStorage.setItem('currentUser', JSON.stringify(appUser));
      localStorage.setItem('idToken', idToken);
      localStorage.setItem('accessToken', accessToken);

      await this.obtainTemporaryCredentials(idToken);
      this.schedulePreventiveRefresh(idToken);
      console.log('Sesión refrescada exitosamente');
    } catch (error) {
      console.error('Error al refrescar sesión:', error);
      this.logout();
    }
  }

  // Programar refresh preventivo antes de que expire el token
  private schedulePreventiveRefresh(idToken: string): void {
    try {
      this.clearRefreshTimer();
      
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      const exp = payload.exp * 1000; // Expiración en milisegundos
      const now = Date.now();
      const timeUntilExpiry = exp - now;
      
      // Programar refresh 5 minutos antes de la expiración (mínimo 1 minuto)
      const refreshTime = Math.max(timeUntilExpiry - (5 * 60 * 1000), 60 * 1000);
      
      if (refreshTime > 0) {
        console.log(`Scheduling preventive refresh in ${Math.round(refreshTime / 1000 / 60)} minutes`);
        this.refreshTimer = setTimeout(() => {
          console.log('Executing preventive refresh...');
          this.refreshSession().catch(error => {
            console.error('Error in preventive refresh:', error);
          });
        }, refreshTime);
      }
    } catch (error) {
      console.error('Error scheduling preventive refresh:', error);
    }
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }
  }

  // Generar un code_verifier y code_challenge para PKCE
  private generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const arrayString = String.fromCharCode(...array);
    // Encode to base64 and then to base64url
    const base64 = btoa(arrayString);
    return this.toBase64url(base64);
  }

  private generateCodeChallenge(codeVerifier: string): string {
    const hashed = CryptoJS.SHA256(codeVerifier);
    const base64String = hashed.toString(CryptoJS.enc.Base64);
    return this.toBase64url(base64String);
  }

  private toBase64url(base64: string): string {
    return base64
      .replace(/\+/g, '-') // Replace '+' with '-'
      .replace(/\//g, '_') // Replace '/' with '_'
      .replace(/=+$/, ''); // Remove trailing '='
  }

  // Iniciar el flujo OAuth 2.0 Authorization Code Grant con PKCE
  login(email: string, password: string): Observable<boolean> {
    console.log('Iniciando login con email:', email);

    if (!email || !password) {
      console.error('Email o contraseña vacíos');
      return throwError(() => new Error('El email y la contraseña son requeridos.'));
    }

    // Generar code_verifier y code_challenge para PKCE
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = this.generateCodeChallenge(codeVerifier);

    // Guardar el code_verifier y las credenciales en localStorage para usarlas después
    localStorage.setItem('code_verifier', codeVerifier);
    localStorage.setItem('oauth_email', email);
    localStorage.setItem('oauth_password', password);

    // Generar un state único para evitar ataques CSRF
    const state = this.generateCodeVerifier();

    // Construir la URL para el endpoint /oauth2/authorize
    const authorizeUrl = new URL(`${this.cognitoDomain}/oauth2/authorize`);
    authorizeUrl.searchParams.append('response_type', 'code');
    authorizeUrl.searchParams.append('client_id', this.clientId);
    authorizeUrl.searchParams.append('redirect_uri', this.redirectUri);
    //authorizeUrl.searchParams.append('redirect_uri', environment.redirectUri);
    authorizeUrl.searchParams.append('scope', 'email openid profile');
    authorizeUrl.searchParams.append('code_challenge', codeChallenge);
    authorizeUrl.searchParams.append('code_challenge_method', 'S256');
    authorizeUrl.searchParams.append('state', state);

    // Redirigir al usuario al endpoint de autorización
    window.location.href = authorizeUrl.toString();

    // Retornar un Observable que indica que el flujo ha iniciado
    return from([true]).pipe(
      map(() => true),
      catchError((error: any) => {
        let errorMessage = 'Error al iniciar el flujo OAuth. Por favor, intenta de nuevo.';
        console.error('Error al iniciar el flujo OAuth:', error);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  handleOAuthCallback(code: string, state: string): Observable<boolean> {
    console.log('Iniciando handleOAuthCallback con code:', code, 'y state:', state);

    const codeVerifier = localStorage.getItem('code_verifier');
    const storedState = localStorage.getItem('state');

    if (!codeVerifier) {
      console.error('Code verifier no encontrado en localStorage');
      return throwError(() => new Error('Code verifier no encontrado'));
    }

    if (storedState && state !== storedState) {
      console.error('State no coincide');
      return throwError(() => new Error('State no coincide'));
    }

    const tokenUrl = `${this.cognitoDomain}/oauth2/token`;
    console.log('Enviando solicitud a tokenUrl:', tokenUrl);

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: this.clientId,
      code: code,
      redirect_uri: this.redirectUri,
      //redirect_uri: environment.redirectUri,
      code_verifier: codeVerifier,
    });
    console.log('Cuerpo de la solicitud a /oauth2/token:', body.toString());

    return this.http
      .post(tokenUrl, body.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .pipe(
        tap((response) => {
          console.log('Solicitud a /oauth2/token exitosa. Respuesta:', response);
        }),
        switchMap((response: any) => {
          // Manejar MFA
          if (response.ChallengeName === 'SOFTWARE_TOKEN_MFA' || response.ChallengeName === 'SMS_MFA') {
            localStorage.setItem(
              'mfa_session',
              JSON.stringify({
                username: response.ChallengeParameters?.USER_ID_FOR_SRP || '',
                Session: response.Session,
              })
            );
            console.log('MFA requerido, redirigiendo a /mfa');
            return of(false);
          }

          const idToken = response.id_token;
          const accessToken = response.access_token;
          const refreshToken = response.refresh_token;

          if (!idToken || !accessToken) {
            console.error('No tokens disponibles');
            throw new Error('No tokens disponibles');
          }

          let idTokenPayload;
          try {
            idTokenPayload = JSON.parse(atob(idToken.split('.')[1]));
            console.log('Payload del idToken:', idTokenPayload);
          } catch (parseError) {
            console.error('Error al parsear el idToken:', parseError);
            throw new Error('Error al parsear el idToken');
          }

          const appUser: User = {
            id: idTokenPayload['sub'] || '0',
            username: idTokenPayload['email'] || 'unknown',
            email: idTokenPayload['email'] || 'unknown',
            role: idTokenPayload['custom:ciso-level'] || 'USER',
            entityId: 'unknown',
            entityName: idTokenPayload['custom:entity'] || 'unknown',
            clientId: idTokenPayload['custom:client-id'] || 'unknown',
          };
          console.log('Usuario creado:', appUser);

          this.currentUserSubject.next(appUser);
          localStorage.setItem('currentUser', JSON.stringify(appUser));
          localStorage.setItem('idToken', idToken);
          localStorage.setItem('accessToken', accessToken);
          if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken);
          }

          localStorage.removeItem('code_verifier');
          localStorage.removeItem('state');

          localStorage.removeItem('oauth_email');
          localStorage.removeItem('oauth_password');

          // Programar refresh preventivo
          this.schedulePreventiveRefresh(idToken);

          return of(true);
        }),
        catchError((error: any) => {
          console.error('Error en la solicitud a /oauth2/token:', error);
          let errorMessage = 'Error al intercambiar el código por tokens: ' + (error.message || 'Desconocido');
          if (error.error && error.error.error) {
            errorMessage += ` - ${error.error.error}: ${error.error.error_description || 'Sin descripción'}`;
          } else if (error.status === 0) {
            errorMessage += ' - Posible problema de CORS o red. Verifica la pestaña Network.';
          }
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  private authenticateWithSRP(email: string, password: string): Observable<any> {
    return from(
      this.client.send(
        new AdminCreateUserCommand({
          UserPoolId: this.userPoolId,
          Username: email,
          UserAttributes: [
            { Name: 'email', Value: email },
            { Name: 'email_verified', Value: 'true' },
          ],
          TemporaryPassword: password,
          MessageAction: 'SUPPRESS',
        })
      )
    ).pipe(
      catchError((error: any) => {
        let errorMessage = 'Error al autenticar el usuario. Por favor, intenta de nuevo.';
        console.error('Error al autenticar con SRP:', error);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  async obtainTemporaryCredentials(idToken: string): Promise<void> {
    console.log('Iniciando obtainTemporaryCredentials con idToken:', idToken.substring(0, 10) + '...');
    const logins = {
      [`cognito-idp.${this.region}.amazonaws.com/${this.userPoolId}`]: idToken,
    };
    console.log('Logins configurados:', Object.keys(logins));

    try {
      const credentialsProvider = fromCognitoIdentityPool({
        clientConfig: { region: this.region },
        identityPoolId: this.identityPoolId,
        logins: logins,
      });
      console.log('CredentialsProvider configurado para Identity Pool:', this.identityPoolId);

      const credentials = await credentialsProvider();
      console.log('Credenciales temporales obtenidas:', {
        accessKeyId: credentials.accessKeyId,
        sessionToken: credentials.sessionToken,
        expiration: credentials.expiration,
      });

      this.client = new CognitoIdentityProviderClient({
        region: this.region,
        credentials: credentials,
      });
      console.log('Cliente de Cognito actualizado');
    } catch (error: any) {
      console.error('Error al obtener credenciales temporales:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        code: error.code,
      });
      throw new Error(`Failed to obtain temporary credentials: ${error.message}`);
    }
  }

  async logout(): Promise<void> {
    // Limpiar timers
    this.clearRefreshTimer();
    
    // Limpiar estado local
    this.currentUserSubject.next(null);
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('currentUser');
    // Limpiar preferencias de File Management
    try {
      localStorage.removeItem('fileUpload.selectedCategory');
      localStorage.removeItem('fileUpload.selectedType');
      localStorage.removeItem(EVOLUTION_CATEGORY_STORAGE_KEY);
      localStorage.removeItem(EVOLUTION_TIME_RANGE_STORAGE_KEY);
    } catch (_) {
      // Ignorar si localStorage no está disponible
    }
    this.regionService.clearPersistedState();
    //this.client = null;
    console.log('Sesión local cerrada');

    const logoutUrl = new URL(`${this.cognitoDomain}/logout`);
    logoutUrl.searchParams.append('client_id', this.clientId);
    logoutUrl.searchParams.append('logout_uri', this.redirectUri);
    logoutUrl.searchParams.append('redirect_uri', this.redirectUri);
    //logoutUrl.searchParams.append('logout_uri', environment.redirectUriLogout);
    //logoutUrl.searchParams.append('redirect_uri', environment.redirectUriLogout);

    console.log('Redirigiendo al endpoint de logout de Cognito:', logoutUrl.toString());
    window.location.href = logoutUrl.toString();
  }

  private clearSession(): void {
    this.currentUserSubject.next(null);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem(EVOLUTION_CATEGORY_STORAGE_KEY);
    localStorage.removeItem(EVOLUTION_TIME_RANGE_STORAGE_KEY);
    this.regionService.clearPersistedState();
    this.client = new CognitoIdentityProviderClient({
      region: this.region,
    });
  }

  public getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  async isAuthenticated(): Promise<boolean> {
    const idToken = localStorage.getItem('idToken');
    if (!idToken) return false;
    try {
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      const exp = payload.exp * 1000;
      const now = Date.now();
      if (now >= exp) {
        await this.refreshToken().toPromise();
      }
      if (!this.client.config.credentials) {
        await this.obtainTemporaryCredentials(idToken);
      }
      return !!this.getCurrentUser();
    } catch (error) {
      console.error('Error al verificar autenticación:', error);
      return false;
    }
  }

  getUserRole(): string | null {
    const user = this.getCurrentUser();
    return user ? user.role : null;
  }

  isUserInAdminsGroup(): boolean {
    try {
      const idToken = localStorage.getItem('idToken');
      if (!idToken) return false;
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      const groups = payload['cognito:groups'] || [];
      return groups.includes('admins');
    } catch (error) {
      console.error('Error al verificar el grupo del usuario:', error);
      return false;
    }
  }

  async getUsers(): Promise<User[]> {
    try {
      const idToken = localStorage.getItem('idToken');
      if (!idToken) {
        throw new Error('No se encontró un token de sesión. Por favor, inicia sesión nuevamente.');
      }

      // 1. Decodificar el token para obtener el client-id
      const decodedToken = jwtDecode<DecodedIdToken>(idToken);
      const tokenClientId = decodedToken['custom:client-id'];
      if (!tokenClientId) {
        throw new Error('No se encontró el atributo custom:client-id en el token');
      }

      // 2. Obtener credenciales temporales
      await this.obtainTemporaryCredentials(idToken);

      // 3. Verificar que el usuario esté autorizado
      const isAdmin = this.isUserInAdminsGroup();
      if (!isAdmin) {
        throw new Error('El usuario no tiene permisos para listar usuarios');
      }

      // 4. Obtener usuarios desde Cognito
      const command = new ListUsersCommand({
        UserPoolId: this.userPoolId,
      });
      const response = await this.client.send(command);
      const users: User[] = [];

      if (response.Users) {
        for (const cognitoUser of response.Users) {
          const clientIdAttr = cognitoUser.Attributes?.find((attr) => attr.Name === 'custom:client-id');
          if (clientIdAttr?.Value !== tokenClientId) {
            continue; // No coincide con el client-id del token, se ignora
          }

          const emailAttr = cognitoUser.Attributes?.find((attr) => attr.Name === 'email');
          const roleAttr = cognitoUser.Attributes?.find((attr) => attr.Name === 'custom:ciso-level');
          const subAttr = cognitoUser.Attributes?.find((attr) => attr.Name === 'sub');
          const displayNameAttr = cognitoUser.Attributes?.find((attr) => attr.Name === 'custom:display_name');
          const userRole = roleAttr?.Value || 'USER';
          if (userRole === 'ADMINISTRATOR') {
            continue;
          }

          users.push({
            id: subAttr?.Value || '',
            username: displayNameAttr?.Value || cognitoUser.Username || '',
            email: emailAttr?.Value || '',
            role: userRole,
            entityId: cognitoUser.Attributes?.find((attr) => attr.Name === 'custom:entity')?.Value || '',
            entityName: '',
            clientId: clientIdAttr?.Value || '',
          });
        }
      }

      return users;
    } catch (error) {
      console.error('Error al obtener usuarios de Cognito:', error);
      throw error;
    }
  }

async addUser(user: User): Promise<User> {
  try {
    if (!this.client.config.credentials) {
      const idToken = localStorage.getItem('idToken');
      if (idToken) {
        await this.obtainTemporaryCredentials(idToken);
      } else {
        throw new Error('No se encontró un token de sesión. Por favor, inicia sesión nuevamente.');
      }
    }
    if (!this.isUserInAdminsGroup()) {
      throw new Error('El usuario no tiene permisos para crear usuarios');
    }
    if (!user.password) {
      throw new Error('La contraseña es requerida para crear un usuario en Cognito.');
    }

    // 🔽 Normalizamos email y lo usamos como username
    const email = this.normalizeEmail(user.email);
    const username = email;

    // 1) Crear usuario SIN TemporaryPassword (Cognito genera una)
    await this.client.send(new AdminCreateUserCommand({
      UserPoolId: this.userPoolId,
      Username: username,
      UserAttributes: [
        { Name: 'email', Value: email },
        { Name: 'name', Value: email },
        { Name: 'email_verified', Value: 'false' },
        { Name: 'custom:ciso-level', Value: user.role },
        { Name: 'custom:entity', Value: user.entityId },
        { Name: 'custom:client-id', Value: user.clientId },
        { Name: 'custom:display_name', Value: user.username },
      ],
      MessageAction: 'SUPPRESS',
    }));

    // 2) Fijar contraseña (permanent = !forcePasswordChange)
    // Default: Si no se especifica forcePasswordChange, asumimos false (no forzar)
    const shouldForcePasswordChange = user.forcePasswordChange === true;
    await this.client.send(new AdminSetUserPasswordCommand({
      UserPoolId: this.userPoolId,
      Username: username,
      Password: user.password,
      Permanent: !shouldForcePasswordChange, // Si no fuerza cambio = permanent, si fuerza cambio = temporary
    }));

    return {
      clientId: user.clientId,
      entityName: user.entityName,
      entityId: user.entityId,
      id: username, // luego lo usas para addUserToGroup
      username: user.username,
      email: email,
      role: user.role,
    };

  } catch (error: any) {
    const code = error?.name || error?.__type || error?.code || '';

    // Si falló al fijar la contraseña, elimina el usuario creado
    if (code === 'PasswordHistoryPolicyViolationException' ||
        code === 'InvalidPasswordException' ||
        code === 'NotAuthorizedException') {
      try {
        await this.client.send(new AdminDeleteUserCommand({
          UserPoolId: this.userPoolId,
          Username: this.normalizeEmail(user.email), // 🔽 normalizado
        }));
      } catch (cleanupErr) {
        console.warn('Rollback falló, el usuario podría haber quedado en el pool:', cleanupErr);
      }
    }

    if (code === 'PasswordHistoryPolicyViolationException') {
      // Contraseña ya usada en el historial del usuario
      throw Object.assign(new Error('Esta contraseña ya fue usada anteriormente. Elige una distinta.'), { name: code });
    }
    if (code === 'InvalidPasswordException') {
      throw Object.assign(new Error('La contraseña no cumple la política del pool.'), { name: code });
    }
    if (code === 'UsernameExistsException') {
      throw Object.assign(new Error('El usuario ya existe.'), { name: code });
    }
    throw error;
  }
}


  async updateUser(updatedUser: User): Promise<void> {
    try {
      if (!this.client.config.credentials) {
        const idToken = localStorage.getItem('idToken');
        if (idToken) {
          await this.obtainTemporaryCredentials(idToken);
        } else {
          throw new Error('No se encontró un token de sesión. Por favor, inicia sesión nuevamente.');
        }
      }
      const isAdmin = this.isUserInAdminsGroup();
      if (!isAdmin) {
        throw new Error('El usuario no tiene permisos para actualizar usuarios');
      }

      // 🔽 Normalizamos email para actualizar atributos
      const email = this.normalizeEmail(updatedUser.email);

      const userAttributes = [
        { Name: 'custom:ciso-level', Value: updatedUser.role },
        { Name: 'custom:entity', Value: updatedUser.entityId },
        { Name: 'custom:client-id', Value: updatedUser.clientId },
        { Name: 'email', Value: email },
        { Name: 'name', Value: email },
        { Name: 'custom:display_name', Value: updatedUser.username },
      ];
      const command = new AdminUpdateUserAttributesCommand({
        UserPoolId: this.userPoolId,
        Username: email,
        UserAttributes: userAttributes,
      });
      await this.client.send(command);
    } catch (error) {
      console.error('Error al actualizar usuario en Cognito:', error);
      throw error;
    }
  }

  async deleteUser(userId: string): Promise<void> {
    try {
      if (!this.client.config.credentials) {
        const idToken = localStorage.getItem('idToken');
        if (idToken) {
          await this.obtainTemporaryCredentials(idToken);
        } else {
          throw new Error('No se encontró un token de sesión. Por favor, inicia sesión nuevamente.');
        }
      }
      const isAdmin = this.isUserInAdminsGroup();
      if (!isAdmin) {
        throw new Error('El usuario no tiene permisos para eliminar usuarios');
      }
      const user = (await this.getUsers()).find((u) => u.id === userId);
      if (!user) throw new Error('Usuario no encontrado');
      const command = new AdminDeleteUserCommand({
        UserPoolId: this.userPoolId,
        Username: this.normalizeEmail(user.email), // 🔽 normalizado
      });
      await this.client.send(command);
    } catch (error) {
      console.error('Error al eliminar usuario de Cognito:', error);
      throw error;
    }
  }

  refreshToken(): Observable<{ idToken: string; accessToken: string; expiresIn: number }> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    const tokenUrl = `${this.cognitoDomain}/oauth2/token`;
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: this.clientId,
      refresh_token: refreshToken,
    });

    return this.http
      .post(tokenUrl, body.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .pipe(
        map((response: any) => {
          const idToken = response.id_token;
          const accessToken = response.access_token;

          if (!idToken || !accessToken) {
            throw new Error('No tokens available in session');
          }

          const idTokenPayload = JSON.parse(atob(idToken.split('.')[1]));
          const expiresIn = idTokenPayload.exp ? Math.floor(idTokenPayload.exp - Date.now() / 1000) : 0;

          localStorage.setItem('idToken', idToken);
          localStorage.setItem('accessToken', accessToken);

          return { idToken, accessToken, expiresIn };
        }),
        tap(() => this.obtainTemporaryCredentials(localStorage.getItem('idToken')!)),
        catchError((error: any) => {
          console.error('Error al refrescar el token:', error);
          return throwError(() => new Error('Error al refrescar el token: ' + error.message));
        })
      );
  }

  generateAuthorizeUrl(): string {
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = this.generateCodeChallenge(codeVerifier);
    localStorage.setItem('code_verifier', codeVerifier);
    const state = this.generateCodeVerifier();

    const authorizeUrl = new URL(`${this.cognitoDomain}/login`);
    authorizeUrl.searchParams.append('response_type', 'code');
    authorizeUrl.searchParams.append('client_id', this.clientId);
    authorizeUrl.searchParams.append('redirect_uri', this.redirectUri);
    //authorizeUrl.searchParams.append('redirect_uri', environment.redirectUri);
    authorizeUrl.searchParams.append('scope', 'email openid profile');
    authorizeUrl.searchParams.append('code_challenge', codeChallenge);
    authorizeUrl.searchParams.append('code_challenge_method', 'S256');
    authorizeUrl.searchParams.append('state', state);

    return authorizeUrl.toString();
  }

  async addUserToGroup(userId: string, groupName: string): Promise<void> {
    try {
      const command = new AdminAddUserToGroupCommand({
        UserPoolId: this.userPoolId,
        Username: userId,
        GroupName: groupName,
      });
      await this.client.send(command);
      console.log(`Usuario ${userId} agregado al grupo ${groupName}`);
    } catch (error) {
      console.error(`Error al agregar usuario ${userId} al grupo ${groupName}:`, error);
      throw error;
    }
  }

}
